import React, { lazy } from 'react';
import { MaterialProps, FinishesProps, CoatingsProps, QuoteLineItemProps } from '@partsbadger/types';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin, Typography } from 'antd';

import { isStlPart } from '@/utils/stlManufacturing';

const { Text } = Typography;

/* Lazy-loaded step components (named exports wrapped for React.lazy) */
const ProcessStep = lazy(() => import('../../').then(m => ({ default: m.ProcessStep })));

const MaterialStep = lazy(() => import('../../').then(m => ({ default: m.MaterialStep })));

const FinishStep = lazy(() => import('../../').then(m => ({ default: m.FinishStep })));

const CoatingStep = lazy(() => import('../../').then(m => ({ default: m.CoatingStep })));

interface StepRendererProps {
    currentStep: number;
    part: QuoteLineItemProps;
    materialsList: MaterialProps[];
    topMaterials: MaterialProps[];
    finishesList: FinishesProps[];
    coatingsList: CoatingsProps[];
    topCoatings: CoatingsProps[];
    isLoading: boolean;
    suggestionsLoading?: boolean;
    isHobbyist: boolean;
    handleSubmit: (payload: any) => void;
    onPendingCustomChange?: (stepIndex: number, isPending: boolean) => void;
    onAdvanceStep?: () => void;
}

/* Named function so React.memo has a display name */
const StepRenderer = React.memo(function StepRenderer({
    currentStep,
    part,
    materialsList,
    topMaterials,
    finishesList,
    coatingsList,
    topCoatings,
    isLoading,
    suggestionsLoading = false,
    isHobbyist,
    handleSubmit,
    onPendingCustomChange,
    onAdvanceStep,
}: StepRendererProps) {
    const lockProcessTo3dPrinting = isStlPart(part);
    const suggestionLoadingNotice = suggestionsLoading ? (
        <div
            className="mt-3 flex items-center gap-2"
            data-testid="drawing-suggestions-loading"
            aria-live="polite"
            style={{ color: '#8c8c8c', fontSize: 11, opacity: 0.75 }}
        >
            <Spin indicator={<LoadingOutlined spin style={{ color: '#bfbfbf', fontSize: 12 }} />} size="small" />
            <Text type="secondary" style={{ fontSize: 11 }}>
                Loading suggestions…
            </Text>
        </div>
    ) : null;

    switch (currentStep) {
        case 0:
            return (
                <ProcessStep
                    selected={part.manufacturing_type}
                    lockTo3dPrinting={lockProcessTo3dPrinting}
                    onNext={lockProcessTo3dPrinting ? onAdvanceStep : undefined}
                    setSelections={newSelections => {
                        const currentMaterial = part?.product?.material_related;

                        const hasRealSelectedMaterial = Boolean(currentMaterial?.id);

                        const currentMaterialStillValid = !hasRealSelectedMaterial
                            ? true
                            : Array.isArray(currentMaterial?.manufacturing_types) &&
                              currentMaterial.manufacturing_types.some(
                                  type => type.toLowerCase() === newSelections.toLowerCase()
                              );

                        const payload: any = {
                            id: part.id,
                            manufacturing_type: newSelections,
                        };

                        if (!currentMaterialStillValid) {
                            payload.product = {
                                material: null,
                                custom_material: '',
                            };
                            payload.material = null;
                        }

                        handleSubmit(payload);
                    }}
                />
            );
        case 1:
            return (
                <>
                    <MaterialStep
                        stepIndex={1}
                        materials={materialsList}
                        topMaterials={topMaterials}
                        materialSelected={part?.product?.material_related}
                        isHobbyist={isHobbyist}
                        manufacturing_type={part.manufacturing_type}
                        loading={isLoading}
                        customMaterial={part.product?.custom_material}
                        onHandleUpdate={product => handleSubmit({ id: part.id, ...product })}
                        onPendingCustomChange={onPendingCustomChange}
                    />
                    {suggestionLoadingNotice}
                </>
            );

        case 2:
            return (
                <>
                    <FinishStep
                        stepIndex={2}
                        finishes={finishesList}
                        finishSelected={part.finish}
                        isHobbyist={isHobbyist}
                        loading={isLoading}
                        customFinish={part.product?.custom_finish}
                        onHandleUpdate={product => handleSubmit({ id: part.id, ...product })}
                        onPendingCustomChange={onPendingCustomChange}
                    />
                    {suggestionLoadingNotice}
                </>
            );

        case 3:
            return (
                <>
                    <CoatingStep
                        stepIndex={3}
                        coatings={coatingsList}
                        topCoatings={topCoatings}
                        coatingSelected={part?.product?.coating_related}
                        isHobbyist={isHobbyist}
                        loading={isLoading}
                        customCoating={part.product?.custom_coating}
                        onHandleUpdate={product => handleSubmit({ id: part.id, ...product })}
                        onPendingCustomChange={onPendingCustomChange}
                    />
                    {suggestionLoadingNotice}
                </>
            );

        default:
            return null;
    }
});

export default StepRenderer;
